// Feature flags and business constants for the therapist schedule/slot picker.
// Ultracite: no `enum` — use `as const`; no `any`/`unknown`.

export const SLOT_SOURCE = {
	/** Use available_slots already embedded in the /available response. 1 request. */
	AVAILABLE_ONLY: "AVAILABLE_ONLY",
	/** Fetch /therapists/{id}/slots per therapist for richer detail. N+1 requests. */
	PER_THERAPIST_DETAIL: "PER_THERAPIST_DETAIL",
} as const;
export type SlotSource = (typeof SLOT_SOURCE)[keyof typeof SLOT_SOURCE];
export const ACTIVE_SLOT_SOURCE: SlotSource = SLOT_SOURCE.AVAILABLE_ONLY;

export const CONTINUITY_MODE = {
	/** Suggest & color the continuity therapist's calendar (Visit 2++ default). */
	ON: "ON",
	/** Always show all available therapists (Visit 1 behavior). */
	OFF: "OFF",
} as const;
export type ContinuityMode =
	(typeof CONTINUITY_MODE)[keyof typeof CONTINUITY_MODE];
export const DEFAULT_CONTINUITY_MODE: ContinuityMode = CONTINUITY_MODE.ON;

// The NEW-vs-legacy master switch is a DB feature flag
// (`USE_NEW_SCHEDULE_PICKER` → `adminPortal.featureFlags.useNewSchedulePickerEnabled`),
// read in the appointment form wiring — not a constant here.

/**
 * Employment-type filter (sits ABOVE the calendar; flow: filter → date → slot).
 * Values match the Therapist model enum (`KARPIS` / `FLAT`); `FLAT` is labelled
 * "MITRA" in the UI. Maps to the external `therapist_type` param — see
 * `employment-type.ts` (KARPIS → internal, FLAT → external).
 */
export const EMPLOYMENT_TYPE = { KARPIS: "KARPIS", FLAT: "FLAT" } as const;
export type EmploymentType =
	(typeof EMPLOYMENT_TYPE)[keyof typeof EMPLOYMENT_TYPE];
export const DEFAULT_EMPLOYMENT_TYPE: EmploymentType = EMPLOYMENT_TYPE.KARPIS;
export const EMPLOYMENT_TYPE_OPTIONS = [
	{ value: EMPLOYMENT_TYPE.KARPIS, label: "KARPIS" },
	{ value: EMPLOYMENT_TYPE.FLAT, label: "MITRA" },
] as const;

/** Default max horizon (weeks) for the selectable date range. */
export const DEFAULT_MAX_VISIT_WEEKS = 8;

/** Slot granularity (minutes) sent as `slot_minutes` to the availability endpoints. */
export const DEFAULT_SLOT_MINUTES = 30;

/**
 * Anchor hours for the compact quick-time row, shared by BOTH schedule modes:
 * the calendar mode's therapist cards and the legacy "cari terapis" picker
 * (`UNIQUE_TIME_SLOTS` in `appointment/form/date-time.tsx` re-exports this).
 * Caps how tall either time list can grow — see `quick-slots.ts`.
 */
export const QUICK_SLOT_ANCHORS = [
	"09:00",
	"11:00",
	"13:00",
	"16:00",
	"18:00",
] as const;

/**
 * Positive availability set. Used for BOTH endpoint A and C (§7): filter on the
 * positive set rather than enumerating negatives (A uses FULL, C uses
 * FULLY_BOOKED — do not enumerate them).
 */
export const BOOKABLE_STATUSES = ["AVAILABLE", "PARTIALLY_BOOKED"] as const;
